import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getCurrentUser,
  getSelectedMover,
  updateSelectedMover,
} from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { CheckIcon, ArrowRightIcon, CheckCheckIcon } from "lucide-react";
import { Link } from "react-router-dom";

// Define the onboarding steps
const ONBOARDING_STEPS = [
  {
    key: "company_info",
    label: "Company information",
    path: "/mover-settings/company-information",
    time_to_complete: 1,
  },
  {
    key: "operating_policies",
    label: "How you operate",
    path: "/mover-settings/operating-policies",
    time_to_complete: 3,
  },
  {
    key: "notifications",
    label: "Leads and moves notifications",
    path: "/mover-settings/basic-configuration",
    time_to_complete: 1,
  },
  {
    key: "labor_rates",
    label: "Labor rates",
    path: "/mover-settings/labor-rates",
    time_to_complete: 3,
  },
  {
    key: "fees",
    label: "Additional Fees",
    path: "/mover-settings/fees",
    time_to_complete: 3,
  },
  {
    key: "invite_users",
    label: "Invite users (you can do this later)",
    path: "/mover-settings/users",
    time_to_complete: 2,
  },
  {
    key: "crew",
    label: "Add your crew (you can do this later)",
    path: "/mover-settings/crew",
    time_to_complete: 5,
  },
  {
    key: "trucks",
    label: "Add your trucks (you can do this later)",
    path: "/mover-settings/trucks",
    time_to_complete: 2,
  },
];

const Onboarding: React.FC = () => {
  const dispatch = useDispatch();
  const mover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();
  const currentUser = useSelector(getCurrentUser);

  if (!mover) {
    return <div>Loading...</div>;
  }

  const completedActions = mover.mover_setup?.onboarding_actions_done || [];

  const handleMarkComplete = async (key: string) => {
    if (isLoading) return;

    try {
      // Create a new array with the current action added
      const updatedActions = [...completedActions, key];

      const updatedMover = await updateMoverApi({
        data: {
          mover_setup_attributes: {
            id: mover.mover_setup.id,
            onboarding_actions_done: updatedActions,
          },
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Progress updated successfully",
      });
    } catch (error) {
      console.error("Error updating onboarding progress:", error);
      toast({
        title: "Error",
        description: "Failed to update progress. Please try again.",
        variant: "destructive",
      });
    }
  };

  const totalTimeToComplete = ONBOARDING_STEPS.reduce(
    (acc, step) => acc + step.time_to_complete,
    0
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-3 text-fuchsia-800">
        Hi {currentUser?.first_name}, let's get you setup!{" "}
      </h1>
      <p className="w-full md:max-w-xl mb-6">
        You'll need just {totalTimeToComplete} minutes to get going, but don't
        worry if you can't finish it in one sitting.
      </p>

      <p className="text-sm mb-2 text-right text-black">
        Time to complete: {totalTimeToComplete} minutes
      </p>
      <div className="space-y-2">
        {ONBOARDING_STEPS.map((step) => {
          const isCompleted = completedActions.includes(step.key);

          return (
            <div
              key={step.key}
              className="flex items-center justify-between px-4 py-3 border rounded-lg bg-white shadow-sm"
            >
              <div className="flex items-center gap-3">
                {isCompleted ? (
                  <div className="w-6 h-6 rounded-full bg-green-700 flex items-center justify-center">
                    <CheckIcon className="w-4 h-4 text-white" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full border border-gray-300" />
                )}
                <Link
                  to={step.path}
                  className="text-lg font-medium hover:underline hover:underline-offset-4 hover:decoration-yellow-500 space-x-2"
                >
                  <span>{step.label}</span>
                </Link>
              </div>

              <div className="flex items-center gap-4">
                {!isCompleted && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-green-50 text-green-700 border-green-600 hover:bg-green-700 hover:text-white"
                    onClick={() => handleMarkComplete(step.key)}
                    disabled={isLoading}
                  >
                    <CheckCheckIcon className="mr-2 h-4 w-4" />I did it
                  </Button>
                )}
                <Link to={step.path}>
                  <Button variant="outline" size="sm" className="border-black">
                    Go to setup
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <div className="text-sm text-gray-600">
                  {step.time_to_complete} min
                </div>
                {/* {isCompleted ? (
                  <div className="flex items-center gap-1 text-green-700 text-sm font-semibold">
                    <CheckIcon className="w-4 h-4" />
                    <span>Completed</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2"></div>
                )} */}
              </div>
            </div>
          );
        })}
      </div>

      {completedActions.length === ONBOARDING_STEPS.length && (
        <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="text-lg font-semibold text-green-800">
            All setup steps completed!
          </h3>
          <p className="text-green-700">
            You've completed all the onboarding steps. Your account is fully set
            up.
          </p>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
